import { computed, nextTick, ref, watch } from 'vue'

import { apiUrl } from '@/api'
import { addTrailingSlash } from '@/utils'

export function useFilePond(
  apiEndpoint,
  callbacks = {
    onSuccess: () => {},
    onError: (error) => error,
    onData: (data) => data,
  },
  opts = { method: 'POST' },
) {
  const {
    onSuccess = () => {},
    onError = (e) => e,
    onData = (d) => d,
  } = callbacks
  const filepondEl = ref()
  apiEndpoint = ref(apiEndpoint)
  const filepondRef = (el) => (filepondEl.value = el)
  const filepondUrl = computed(
    () => `${addTrailingSlash(apiUrl)}${apiEndpoint.value}`,
  )

  function getUploadFileFormData(fieldName, fileData, fileName) {
    const formData = new FormData()
    if (fileName) formData.append(fieldName, fileData, fileName)
    else formData.append(fieldName, fileData)
    return formData
  }

  function filepondProcess(
    fieldName,
    file,
    metadata,
    load,
    error,
    progress,
    abort,
    // transfer,
    // options,
  ) {
    // See https://pqina.nl/filepond/docs/api/server/#process-1
    let formData = getUploadFileFormData(fieldName, file, file.name)
    formData = onData(formData)

    const request = new XMLHttpRequest()
    request.open(opts.method, filepondUrl.value)
    // Add the logged in user token to the request headers
    const bearerToken = localStorage.getItem('token')
    request.setRequestHeader('Authorization', `Bearer ${bearerToken}`)

    // Should call the progress method to update the progress to 100% before calling load
    // Setting computable to false switches the loading indicator to infinite mode
    request.upload.onprogress = (e) => {
      progress(e.lengthComputable, e.loaded, e.total)
    }

    // Should call the load method when done and pass the returned server file id
    // this server file id is then used later on when reverting or restoring a file
    // so your server knows which file to return without exposing that info to the client
    request.onload = function () {
      if (request.status >= 200 && request.status < 300) {
        // the load method accepts either a string (id) or an object
        const responseObject = JSON.parse(request.response)
        onSuccess(responseObject)
        load(request.responseText)
      } else {
        // Can call the error method if something is wrong, should exit after
        let responseObject = { message: 'Error uploading file' }
        try {
          responseObject = JSON.parse(request.response)
        } catch (parseError) {
          console.warn('Could not parse upload error response', parseError)
        }
        onError(responseObject)
        error(responseObject.message)
      }
    }

    request.onerror = function () {}

    request.send(formData)

    return {
      abort: () => {
        request.abort()
        abort()
      },
    }
  }
  const filepondUploadConfig = computed(() => {
    return {
      url: apiEndpoint,
      process: filepondProcess,
      revert: null,
    }
  })

  function removeFileFromDropzone(file) {
    if (filepondEl.value) {
      filepondEl.value.removeFile(file.id)
    }
  }

  const processFileSuccess = ref({})
  function onProcessSuccess(file) {
    delete processFileErrors.value[file.id]
    processFileSuccess.value[file.id] = file
    setTimeout(() => {
      removeFileFromDropzone(file)
      delete processFileSuccess.value[file.id]
    }, 5000)
  }

  const processFileErrors = ref({})
  function onProcessError(error, file) {
    processFileErrors.value[file.id] = file
  }
  const processingFileIds = ref([])
  const addProcessFile = (file) => processingFileIds.value.push(file.id)
  const removeProcessFile = (file) => {
    const iDel = processingFileIds.value.indexOf(file.id)
    iDel !== -1 && processingFileIds.value.splice(iDel, 1)
  }
  watch(
    filepondEl,
    async (el) => {
      if (!el) return
      // Wait for FilePond to initialize its internal _pond instance
      await nextTick()
      if (!el._pond) return
      el._pond.setOptions({
        server: filepondUploadConfig.value,
      })
      el._pond.on('addfile', (error, file) => {
        if (!error) addProcessFile(file)
      })
      el._pond.on('processfile', (error, file) => {
        if (error) onProcessError(error, file)
        else onProcessSuccess(file)
        removeProcessFile(file)
      })
      el._pond.on('processfileabort', (error, file) => {
        removeProcessFile(file)
      })
    },
    { flush: 'post' },
  )

  return {
    filepondEl,
    filepondRef,
    filepondUploadConfig,
    getUploadFileFormData,
    processingFileIds,
    isProcessingFile: computed(() => processingFileIds.value.length > 0),
    processFileSuccess,
    processFileErrors,
    removeFileFromDropzone,
  }
}
